import { __ } from '@utils/i18n';
import { ErrorImage, ReachErrorImage } from '@images';

export const ALLY_LANDING_URL =
	'https://elementor.com/products/ally-web-accessibility/';
export const ALLY_REPO_URL =
	'https://wordpress.org/plugins/pojo-accessibility/';
export const APP_ROOT_ID = 'ea11y-scanner-wizard';
export const CONTENT_ROOT_ID = 'ea11y-scanner-wizard-content';
export const SCANNER_ROOT_ID = 'ea11y-scanner-wizard-widget';

export const CURRENT_ELEMENT_CLASS = 'ea11y-scanner-current-element';
export const COLOR_ELEMENT_CLASS = 'ea11y-scanner-color-element';
export const BACKGROUND_ELEMENT_CLASS = 'ea11y-scanner-background-element';
export const COLOR_CONTRAST_SELECTORS_COUNT = 5;
export const LEVEL_VIOLATION = 'violation';
export const LEVEL_POTENTIAL = 'potentialViolation';
export const RULE_TEXT_CONTRAST = 'text_contrast_sufficient';
export const RATIO_EXCLUDED = 1;

export const BLOCKS = {
	main: 'main',
	management: 'management',
	altText: 'altText',
	dynamicContent: 'dynamicContent',
	formsInputsError: 'formsInputsError',
	keyboardAssistiveTech: 'keyboardAssistiveTech',
	pageStructureNav: 'pageStructureNav',
	headingStructure: 'headingStructure',
	tables: 'tables',
	colorContrast: 'colorContrast',
	other: 'other',
};

export const BLOCK_TITLES = {
	altText: __('Alternative text'),
	dynamicContent: __('Dynamic Content & ARIA'),
	formsInputsError: __('Forms & Input Errors'),
	keyboardAssistiveTech: __('Keyboard & Assistive Technologies'),
	pageStructureNav: __('Page Structure & Navigation'),
	headingStructure: __('Heading Structure'),
	tables: __('Tables'),
	colorContrast: __('Color contrast'),
	other: __('Other Accessibility Issues'),
};

export const BLOCK_INFO = {
	altText: __(
		"Clearly describe images so people using screen readers understand what's displayed.",
	),
	dynamicContent: __(
		'Label interactive content clearly, helping screen reader users navigate dynamic elements.',
	),
	formsInputsError: __(
		"Clearly label form fields and errors so people know exactly what's needed.",
	),
	keyboardAssistiveTech: __(
		'Make sure people can fully use your site with only a keyboard, no mouse required.',
	),
	pageStructureNav: __(
		'Use headings and clear structure to help people easily navigate your content.',
	),
	headingStructure: __(
		'Headings help screen reader users and search engines read pages in the right order. The structure should be organized and each heading given a level.',
	),
	tables: __(
		'Give tables clear headers and captions so everyone can easily understand the data.',
	),
	colorContrast: __(
		'Text and background lightness can hinder readability. Depending on text size, you may need to adjust the contrast level to improve accessibility.',
	),
	other: __(
		'Find and resolve additional accessibility issues to ensure your site works for everyone.',
	),
};

export const INITIAL_SORTED_VIOLATIONS = {
	altText: [],
	dynamicContent: [],
	formsInputsError: [],
	keyboardAssistiveTech: [],
	pageStructureNav: [],
	headingStructure: [],
	tables: [],
	colorContrast: [],
	other: [],
};

export const VIOLATION_TYPES = {
	altText: [
		'applet_alt_exists',
		'applet_alt_exists',
		'img_alt_redundant',
		'img_alt_valid',
		'img_alt_null',
		'area_alt_exists',
		'imagebutton_alt_exists',
		'imagemap_alt_exists',
		'img_alt_decorative',
		'object_text_exists',
		'svg_graphics_labelled',
	],
	dynamicContent: [
		'aria_parent_required',
		'combobox_popup_reference',
		'aria_activedescendant_valid',
		'combobox_active_descendant',
		'aria_role_valid',
		'combobox_autocomplete_valid',
		'combobox_focusable_elements',
		'combobox_haspopup_valid',
		'aria_descendant_valid',
		'aria_role_allowed',
		'aria_attribute_allowed',
		'aria_attribute_conflict',
		'aria_attribute_exists',
		'aria_attribute_required',
		'aria_attribute_value_valid',
		'aria_eventhandler_role_valid',
		'aria_id_unique',
		'aria_widget_labelled',
		'combobox_design_valid',
		'element_id_unique',
		'element_accesskey_unique',
		'aria_attribute_valid',
	],
	formsInputsError: [
		'label_content_exists',
		'input_checkboxes_grouped',
		'fieldset_label_valid',
		'form_label_unique',
		'label_ref_valid',
		'input_autocomplete_valid',
		'error_message_exists',
		'input_label_after',
		'input_label_before',
		'input_label_exists',
	],
	keyboardAssistiveTech: [
		'aria_hidden_nontabbable',
		'aria_activedescendant_tabindex_valid',
		'aria_child_tabbable',
		'element_scrollable_tabbable',
		'iframe_interactive_tabbable',
		'a_text_purpose',
		'label_name_visible',
		'html_lang_exists',
		'html_lang_valid',
		'element_lang_valid',
		'input_haspopup_conflict',
		'element_tabbable_role_valid',
	],
	headingStructure: [
		'single_h1_check',
		'missing_h1_check',
		'heading_hierarchy_check',
	],
	pageStructureNav: [
		'table_headers_ref_valid',
		'table_scope_valid',
		'table_headers_exists',
		'table_headers_related',
		'table_structure_misuse',
		'dir_attribute_valid',
		'aria_application_label_unique',
		'aria_application_labelled',
		'aria_article_label_unique',
		'aria_banner_label_unique',
		'aria_banner_single',
		'aria_complementary_label_unique',
		'aria_complementary_labelled',
		'aria_content_in_landmark',
		'aria_contentinfo_label_unique',
		'aria_contentinfo_single',
		'aria_document_label_unique',
		'aria_form_label_unique',
		'aria_landmark_name_unique',
		'aria_main_label_unique',
		'aria_navigation_label_unique',
		'aria_region_label_unique',
		'aria_region_labelled',
		'aria_search_label_unique',
		'aria_toolbar_label_unique',
		'skip_main_exists',
		'page_title_exists',
		'frame_title_exists',
		'list_children_valid',
		'table_aria_descendants',
	],
	tables: [
		'table_caption_empty',
		'table_caption_nested',
		'table_summary_redundant',
	],
	colorContrast: ['text_spacing_valid', 'text_contrast_sufficient'],
	other: [
		'element_orientation_unlocked',
		'meta_redirect_optional',
		'blink_elem_deprecated',
		'marquee_elem_avoid',
	],
};

export const INSTALL_MODAL_STEPS = Object.freeze({
	INSTALL: 'install',
	SUBSCRIPTION_FORM: 'subscription_form',
});

export const PROXY_ERRORS = Object.freeze({
	BAD_REQUEST: 'bad_request',
	FORBIDDEN: 'forbidden',
	PAYLOAD_TOO_LARGE: 'payload_too_large',
	RATE_LIMITED: 'rate_limited',
	INTERNAL_SERVER_ERROR: 'internal_server_error',
	TOO_MANY_REDIRECTS: 'too_many_redirects',
	GATEWAY_TIMEOUT: 'gateway_timeout',
	BAD_GATEWAY: 'bad_gateway',
	BOT_PROTECTION: 'bot_protection',
});

export const ERROR_CONTENT = {
	CANT_REACH_PAGE: {
		Image: ReachErrorImage,
		title: __("This page can't be reached"),
		message: __(
			"Make sure the site is published and publicly available. If the issue continues, try a different URL.",
		),
	},
	GENERIC_ERROR: {
		Image: ErrorImage,
		title: __('Something seems to be wrong'),
		message: __(
			"We're having trouble reaching this URL. Please try again or use a different one.",
		),
	},
};

export const ERROR_TYPE_MAP = {
	[PROXY_ERRORS.GATEWAY_TIMEOUT]: ERROR_CONTENT.CANT_REACH_PAGE,
	[PROXY_ERRORS.BAD_GATEWAY]: ERROR_CONTENT.CANT_REACH_PAGE,
	[PROXY_ERRORS.BAD_REQUEST]: ERROR_CONTENT.CANT_REACH_PAGE,
	[PROXY_ERRORS.FORBIDDEN]: ERROR_CONTENT.CANT_REACH_PAGE,
	[PROXY_ERRORS.BOT_PROTECTION]: ERROR_CONTENT.GENERIC_ERROR,
	[PROXY_ERRORS.RATE_LIMITED]: ERROR_CONTENT.GENERIC_ERROR,
	[PROXY_ERRORS.TOO_MANY_REDIRECTS]: ERROR_CONTENT.GENERIC_ERROR,
	[PROXY_ERRORS.PAYLOAD_TOO_LARGE]: ERROR_CONTENT.GENERIC_ERROR,
	[PROXY_ERRORS.INTERNAL_SERVER_ERROR]: ERROR_CONTENT.GENERIC_ERROR,
};
